import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { TagModel } from 'models';

import { apiClient } from 'core/api/globals';
import { useEvent } from 'core/hooks';
import { NotificationsService } from 'core/notifications';

import { TagsRoutes } from '../routes';
import { TagsEditService } from '../services';
import { EditFormValues } from '../types';
import { TagsFieldTemplateForm } from './TagsFieldTemplateForm';

// TODO: Fetch actual parent tags if needed
const MOCK_PARENT_TAGS: TagModel[] = [];

export const EditTagsFieldTemplatePage: FunctionComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // Get ID from route params
  const [tagToEdit, setTagToEdit] = useState<TagModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tagId = Number(id);

  // Fetch the tag data when the component mounts or ID changes
  useEffect(() => {
    if (isNaN(tagId) || tagId <= 0) {
      NotificationsService.displayError('Invalid Tag ID.');
      navigate(TagsRoutes.HOME);
      return;
    }

    const fetchTag = async () => {
      setIsLoading(true);
      try {
        // TODO: Verify apiClient method
        const fetchedTag = await apiClient.tagsClient.getTagsById(tagId, 'msal-required');
        setTagToEdit(fetchedTag);
      } catch (error) {
        NotificationsService.displayError('Failed to load tag data.');
        console.error('Fetch tag error:', error);
        navigate(TagsRoutes.HOME);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTag();
  }, [tagId, navigate]);

  const handleSubmit = useEvent(async (formData: EditFormValues) => {
    if (!tagToEdit) return; // Should not happen if loading finished

    setIsSubmitting(true);
    try {
      // Convert form data to TagModel using the service
      const tagModelPayload = TagsEditService.copyFormToModel(tagToEdit.id, formData);

      // Use the correct add/update method
      await apiClient.tagsClient.addUpdateTags(tagModelPayload);
      NotificationsService.displaySuccess('Tag updated successfully');
      navigate(TagsRoutes.HOME); // Navigate back to grid on success
    } catch (error) {
      NotificationsService.displayError('Failed to update tag');
      console.error('Update tag error:', error);
      setIsSubmitting(false); // Keep form enabled on error
    }
  });

  if (isLoading) {
    // TODO: Replace with a proper loading indicator component
    return <div>Loading Tag Data...</div>;
  }

  if (!tagToEdit) {
    // TODO: Replace with a proper error/not found component
    return <div>Tag not found.</div>;
  }

  return (
    <TagsFieldTemplateForm
      tag={tagToEdit} // Pass the fetched tag
      parentTags={MOCK_PARENT_TAGS} // Pass fetched or mock parent tags
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  );
};
